<template>
    <div class="form-container">
        <h2>{{ isEditing ? 'Edit' : 'Add' }} Practitioner</h2>
        <hr>

        <div v-if="errorMessage" class="error-message">
            <p>{{ errorMessage }}</p>
        </div>

        <form @submit.prevent="savePractitioner">
            <!-- Full Name -->
            <label>Full Name <span class="required">*</span></label>
            <input type="text" v-model="practitioner.name" placeholder="Full Name" required />
            <div v-if="errors.name" class="error-message">{{ errors.name[0] }}</div>

            <!-- Email -->
            <label>Email <span class="required">*</span></label>
            <input type="email" v-model="practitioner.email" placeholder="Email" required />
            <div v-if="errors.email" class="error-message">{{ errors.email[0] }}</div>

            <!-- Password -->
            <label>Password <span class="required" v-if="!isEditing">*</span></label>
            <input type="password" v-model="practitioner.password" placeholder="Password" :required="!isEditing" />
            <div v-if="errors.password" class="error-message">{{ errors.password[0] }}</div>

            <!-- Signature -->
            <div class="signature-box">
                <label>Practitioner Signature:</label>
                <VueSignaturePad ref="signaturePad" class="signature-canvas" />
                <button type="button" @click="clearSignature" class="btn-clear mb-2 round-border">Clear
                    Signature</button>
            </div>
            <div v-if="errors.signature" class="error-message">{{ errors.signature[0] }}</div>

            <!-- Buttons -->
            <div class="btn-group mt-3">
                <button type="submit" class="btn btn-sm btn-primary me-2" style="min-width: 150px;">
                    {{ isEditing ? 'Update' : 'Add' }}
                </button>
                <button type="button" class="btn btn-sm btn-secondary cancel-btn" style="min-width: 150px;"
                    @click="$router.push({ name: 'PractitionerList' })">Cancel</button>
            </div>
        </form>
    </div>
</template>

<script>
import { VueSignaturePad } from "vue-signature-pad";

export default {
    components: { VueSignaturePad },
    props: ['id'],
    data() {
        return {
            practitioner: {
                name: '',
                email: '',
                password: '',
                signature: ''
            },
            isEditing: false,
            errorMessage: null,
            errors: {}
        };
    },
    methods: {
        clearSignature() {
            this.$refs.signaturePad.clearSignature();
        },
        saveSignature() {
            const pad = this.$refs.signaturePad;
            if (pad && !pad.isEmpty()) {
                this.practitioner.signature = pad.saveSignature().data;
            }
        },
        async savePractitioner() {
            this.errorMessage = null;
            this.errors = {};
            this.saveSignature();

            try {
                const formData = new FormData();
                formData.append("name", this.practitioner.name);
                formData.append("email", this.practitioner.email);
                if (!this.isEditing) formData.append("password", this.practitioner.password);
                if (this.practitioner.signature) {
                    const blob = this.dataURLtoBlob(this.practitioner.signature);
                    if (blob instanceof Blob && blob.size > 0) {
                        formData.append("signature", blob, "signature.png");
                    }
                }

                if (this.isEditing) {
                    formData.append('_method', 'PUT');
                    await this.$api.post(`/practitioners/${this.id}`, formData, {
                        headers: { ...this.$api.defaults.headers.common }
                    });
                } else {
                    await this.$api.post('/practitioners', formData, {
                        headers: { ...this.$api.defaults.headers.common }
                    });
                }

                this.$router.push({ name: 'PractitionerList' });
            } catch (error) {
                if (error.response?.status === 422) {
                    this.errors = error.response.data.errors;
                } else {
                    this.errorMessage = 'An error occurred while saving the practitioner.';
                }
            }
        },
        dataURLtoBlob(dataURL) {
            if (!dataURL || !dataURL.includes(',')) return null;
            const [header, base64] = dataURL.split(',');
            const mime = header.match(/:(.*?);/)[1];
            const binary = atob(base64);
            const array = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                array[i] = binary.charCodeAt(i);
            }
            return new Blob([array], { type: mime });
        },
        async fetchPractitioner() {
            if (this.id) {
                this.isEditing = true;
                try {
                    const response = await this.$api.get(`/practitioners/${this.id}`);
                    const data = response.data;
                    this.practitioner.name = data.user.name;
                    this.practitioner.email = data.user.email;
                    this.practitioner.signature = data.signature;

                    this.$nextTick(() => {
                        if (this.practitioner.signature && this.$refs.signaturePad) {
                            this.$refs.signaturePad.fromDataURL(this.practitioner.signature);
                        }
                    });
                } catch (error) {
                    this.errorMessage = 'Error fetching practitioner data.';
                }
            }
        }
    },
    mounted() {
        this.fetchPractitioner();
    }
};
</script>

<style scoped>
.form-container {
    padding: 20px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.signature-box {
    border: 1px solid #ccc;
    padding: 10px;
    margin-top: 20px;
    text-align: center;
}

.signature-canvas {
    border: 1px solid #000;
    width: 100%;
    height: 200px;
}

.btn-clear {
    margin-top: 10px;
    background-color: red;
    color: white;
    border: none;
    padding: 5px 10px;
    cursor: pointer;
    border-radius: 5px;
}
</style>
